// eslint-disable-next-line max-lines
import LikeSolid from '@eduzz/houston-icons/LikeSolid';
import IconButton from '@eduzz/houston-ui/IconButton';
import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from 'components/Alert';
import Truncate from 'components/Truncate';
import moment from 'moment';
import React, { memo, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TableOptions from 'v2/components/Shared/TableOptions';
import affiliateService from 'v2/services/affiliate';

import { useStyles } from './styles';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TableReproved = ({ onSort }: IProps) => {
  const classes = useStyles();

  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliatesSelected, setAffiliatesSelected] = useState<Array<number>>([]);

  const onChangeRowsPerPage = (per_page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page, current_page: 1 } });
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
  };

  const resetAffiliatesSelected = () => {
    setAffiliatesSelected([]);
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map((a) => a.id);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (id: number) => {
    let selected = [];

    if (affiliatesSelected.includes(id)) {
      selected = affiliatesSelected.filter((a) => a !== id);
      setAffiliatesSelected(selected);
      return;
    }

    selected = [...affiliatesSelected, id];
    setAffiliatesSelected(selected);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliado aprovado com sucesso.');
    }, toast.error);
  };

  const approvedAffiliates = async () => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: 'Deseja realmente aprovar os afiliados selecionados?',
      confirmation: true,
      buttonOk: 'Aprovar',
      buttonCancel: 'Cancelar',
    });

    if (!confirmation) {
      return;
    }

    affiliateService.moderateMultipleAffiliates(affiliatesSelected, 'A').subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliados aprovados com sucesso.');
      affiliateService.updateListAffiliates();
    }, toast.error);
  };

  return (
    <>
      {!!affiliatesSelected.length && (
        <div className={classes.tableOptionsContainer}>
          <TableOptions
            items={affiliatesSelected.length}
            style={{
              top: 0,
              padding: 16,
            }}
            options={[
              {
                label: 'Cancelar',
                callback: () => resetAffiliatesSelected(),
              },
              {
                icon: 'thumb_up',
                label: 'Aprovar',
                callback: () => approvedAffiliates(),
              },
            ]}
          />
        </div>
      )}

      <Table id='affiliates_table' total={pagination?.total_items || 0} loading={isLoading}>
        <Table.Header sortDirection={currentSort?.direction} sortField={currentSort?.field} onSort={onSort}>
          <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
            <Table.Column width={45}>
              <Checkbox
                indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
                checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
                onChange={selectAllAffiliates}
                disabled={!affiliates?.length}
              />
            </Table.Column>
          </PermissionHide>
          <Table.Column width={170} sortableField='request_date'>
            Data da solicitação
          </Table.Column>
          <Table.Column width={300} sortableField='product_title'>
            Produto
          </Table.Column>
          <Table.Column sortableField='affiliate_name'>Afiliado</Table.Column>
          <Table.Column width={450}>Mensagem</Table.Column>
          <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
            <Table.Column width={80} align='center'>
              Ações
            </Table.Column>
          </PermissionHide>
        </Table.Header>

        <Table.Body>
          {affiliates?.map((affiliate, index) => (
            <Table.Row data={affiliate} key={affiliate.id} index={index}>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <Table.Cell>
                  <Checkbox checked={affiliatesSelected.includes(affiliate.id)} onClick={() => toggleSelect(affiliate.id)} />
                </Table.Cell>
              </PermissionHide>
              <Table.Cell>
                <Typography>{moment(affiliate.request_date).format('L')}</Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs' weight='semibold'>
                  <Truncate count={30} final='...'>
                    {affiliate.product_title}
                  </Truncate>
                </Typography>
                <Typography size='xxs'>({affiliate.product_id})</Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography>
                  <a href={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
                    <Truncate count={25} final={'...'}>
                      {affiliate.affiliate_name}
                    </Truncate>
                  </a>
                </Typography>
              </Table.Cell>
              <Table.Cell>
                <Truncate count={50} final='...'>
                  {affiliate.request_message || '-'}
                </Truncate>
              </Table.Cell>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <Table.Cell align='center'>
                  <Tooltip title='Aprovar' placement='top'>
                    <IconButton onClick={() => handleClickModerate(affiliate.id, 'A')}>
                      <LikeSolid />
                    </IconButton>
                  </Tooltip>
                </Table.Cell>
              </PermissionHide>
            </Table.Row>
          ))}
        </Table.Body>

        {!isLoading && pagination && (
          <Table.Pagination
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            optionsPerPage='10,20,50,100,200'
            onChangePage={onChangePage}
            onChangePerPage={onChangeRowsPerPage}
          />
        )}
      </Table>
    </>
  );
};

export default memo(TableReproved);
